/**
 * NotificationScheduleItem
 *
 * Covers the schedule-content types (timer leading icon, no avatar/user tap):
 *   - scheduleContent          — `notification_schedule_content.dart:37-43`
 *   - scheduleContentPublished — `notification_schedule_content_published.dart:37`
 *
 * scheduleContent title: `Your video has been scheduled[ with Early Access for
 * your subscribers]- {scheduleDate}` (schedule date via `getDate(mediaInfo.schedule)`).
 * scheduleContentPublished title: `Your video has been published`.
 * Subtitle for both: the notification date. Trailing thumbnail from `mediaInfo`.
 */
import React from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { NotificationScheduleKind } from '@/domain/enums/inbox/notificationScheduleKind';
import { NotificationRowLayout } from '../NotificationRowLayout';
import { NotificationLeadingIcon } from './shared/NotificationLeadingIcon';
import { NotificationMediaThumbnail } from './shared/NotificationMediaThumbnail';
import type { NotificationItemProps } from './notificationItemProps';

interface NotificationScheduleItemProps extends NotificationItemProps {
  kind: NotificationScheduleKind;
}

export const NotificationScheduleItem: React.FC<NotificationScheduleItemProps> = ({
  kind,
  notification,
  onItemTap,
  onItemMediaTap,
}) => {
  const { l10n } = useLocale();
  const { formatDate } = useDateFormatters();
  const { theme } = useTheme();
  const n = l10n.inbox.notifications;

  const { info } = notification;
  const mediaInfo = info.mediaInfo;

  let title: string;
  if (kind === NotificationScheduleKind.scheduleContent) {
    title = n.videoScheduled;
    if (mediaInfo?.allowEarlyAccess === true) {
      title += n.videoScheduledEarlyAccessSuffix;
    }
    // Flutter appends `- {getDate(schedule)}` directly (no leading space).
    if (mediaInfo?.schedule != null) {
      title += `- ${formatDate(mediaInfo.schedule)}`;
    }
  } else {
    title = n.videoPublished;
  }

  return (
    <NotificationRowLayout
      leading={
        <NotificationLeadingIcon iconPath="/assets/images/timer.png" iconColor={theme.disabled} />
      }
      title={title}
      subtitle={formatDate(info.timestamp)}
      trailing={mediaInfo ? <NotificationMediaThumbnail mediaInfo={mediaInfo} /> : undefined}
      onItemTap={onItemTap ? () => onItemTap(notification) : undefined}
      onTrailingTap={onItemMediaTap ? () => onItemMediaTap(notification) : undefined}
    />
  );
};
